import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// UTILS
import { validationErrorsToString, parseDate } from '../../utils';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import CircleText from '../../components/CircleText';
import GlobalModal from '../../components/GlobalModal';
import AddAdditionalTrainingModal from './AdditionalTrainingModal';
import EditDeadlineModal from './EditDeadlineModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusIcon from '../../components/StatusIcon';
import DisplayDate from '../../components/DisplayDate';
import RemoveModal from './RemoveModal';
import AssignTrainingModal from './AssignTrainingModal';
import AssignAllTrainingModal from './AssignAllTrainingModal';
import CircleImage from '../../components/CircleImage';

// ICONS
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';
import { ReactComponent as SwitchIcon } from '../../assets/svg/switch.svg';
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import {
	DomainTrainingProfileType,
	TrainingAdminDashboardTrainings,
	TrainingUserDashboardDashboard,
	TrainingEditCommand
} from '../../api/models';

const Training: React.FC = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get all Training Time Frames from Redux store
	const trainingTimeFrames = useAppSelector((state) => state.lists.trainingTimeFrames);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Check if User is viewing Training as an Admin or not
	const viewTrainingAsAdmin = useAppSelector((state) => state.user.viewTrainingAsAdmin);

	// State for Clean up function
	const [isMounted, setIsMounted] = React.useState<boolean>(false);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Set up Admin Data state and initial Admin data
	const initialTrainingData: TrainingAdminDashboardTrainings = {
		modules: [],
		leadership: [],
		staff: [],
		parents: [],
		volunteers: [],
		children: [],
		visitors: []
	};

	const [trainingData, setTrainingData] = React.useState<TrainingAdminDashboardTrainings>(initialTrainingData);

	const getTrainingData = async () => {
		setIsLoading(true);

		const response = await apibridge.getTrainingAdminDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialTrainingData } = response;

			setIsLoading(false);

			setTrainingData(result);
		}
	};

	// Set up User Data state and initial User data
	const initialUserTrainingData: TrainingUserDashboardDashboard = {
		additionalTrainings: [],
		acknowledgements: []
	};

	const [userTrainingData, setUserTrainingData] =
		React.useState<TrainingUserDashboardDashboard>(initialUserTrainingData);

	const getUserTrainingData = async () => {
		setIsLoading(true);

		const response = await apibridge.getTrainingUserDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			const { result = initialUserTrainingData } = response;

			setIsLoading(false);

			setUserTrainingData(result);
		}
	};

	// Check if User follow the Assigned Link from Dashboard
	React.useEffect(() => {
		if (location.pathname === '/training/user-view') {
			dispatch(UserSlice.actions.update({ viewTrainingAsAdmin: false }));

			getUserTrainingData();
		} else if (isOrganisationAdmin && location.pathname === '/training/admin-view') {
			dispatch(UserSlice.actions.update({ viewTrainingAsAdmin: true }));

			getTrainingData();
		}
	}, []);

	// Load data based on which view is loaded
	React.useEffect(() => {
		if (isOrganisationAdmin && viewTrainingAsAdmin) {
			setIsMounted(true);

			getTrainingData();
		} else {
			setIsMounted(true);

			getUserTrainingData();
		}

		setIsLoading(false);
	}, []);

	// Filter Admin Training Data
	const { modules, leadership, staff, parents, children, visitors, volunteers } = trainingData;

	const availableLeadershipModules = modules?.filter(({ id }) => {
		return !leadership?.find((item) => item.moduleId === id);
	});

	const availableStaffModules = modules?.filter(({ id }) => {
		return !staff?.find((item) => item.moduleId === id);
	});

	const availableParentModules = modules?.filter(({ id }) => {
		return !parents?.find((item) => item.moduleId === id);
	});

	const availableVolunteerModules = modules?.filter(({ id }) => {
		return !volunteers?.find((item) => item.moduleId === id);
	});

	const availableChildrenModules = modules?.filter(({ id }) => {
		return !children?.find((item) => item.moduleId === id);
	});

	const availableVisitorModules = modules?.filter(({ id }) => {
		return !visitors?.find((item) => item.moduleId === id);
	});

	// Filter User Training Data
	const { additionalTrainings, acknowledgements } = userTrainingData;

	const uniqueCompletedUserTraining: any = [];

	const completedUserTraining = acknowledgements?.filter((item) => {
		if (item.completed) {
			const isDuplicate = uniqueCompletedUserTraining.includes(item.moduleId);

			if (!isDuplicate) {
				uniqueCompletedUserTraining.push(item.moduleId);

				return true;
			}
			return false;
		}
	});

	// Filter by date and time completed before mapping and display
	completedUserTraining?.sort(function (a, b) {
		let dateA: Date = new Date(a.completed || '');
		let dateB: Date = new Date(b.completed || '');

		return +dateB - +dateA;
	});

	const overdueUserTraining = acknowledgements?.filter(
		(item) => item.deadline && !item.completed && new Date() >= parseDate(item.deadline)
	);

	const availableUserTraining =
		acknowledgements &&
		acknowledgements.filter(({ id, completed }) => {
			return !completed && !overdueUserTraining?.find((item) => item.id === id);
		});

	// State to control active tab and active content
	const [activeTab, setActiveTab] = React.useState<string>('leadership');

	// State to control Modals
	const [showAddTrainingModal, setShowAddTrainingModal] = React.useState<boolean>(false);

	const [showEditDeadlineModal, setShowEditDeadlineModal] = React.useState<boolean>(false);

	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	const [showAssignTrainingModal, setShowAssignTrainingModal] = React.useState<boolean>(false);

	const [showAssignAllTrainingModal, setShowAssignAllTrainingModal] = React.useState<boolean>(false);

	// State to store Asigned Training Id
	const [assignedTrainingId, setAssignedTrainingId] = React.useState<string>('');

	// State to store Asigned Training Id Arrays
	const [assignedTrainingIdArray, setAssignedTrainingIdArray] = React.useState<string[]>([]);

	// State to store Asigned Training Title
	const [assignedTrainingTitle, setAssignedTrainingTitle] = React.useState<string>('');

	const addAllHandler = async (array: string[]) => {
		setIsLoading(true);

		const data: TrainingEditCommand = {
			moduleIds: array,
			profileType: activeTab as DomainTrainingProfileType
		};

		const response = await apibridge.postTrainingEdit(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			getTrainingData();
		}
	};

	const switchRole = () => {
		if (isOrganisationAdmin) {
			if (viewTrainingAsAdmin) {
				dispatch(UserSlice.actions.update({ viewTrainingAsAdmin: false }));

				getUserTrainingData();
			} else {
				dispatch(UserSlice.actions.update({ viewTrainingAsAdmin: true }));

				getTrainingData();
			}
		}
	};

	const [itemData, setItemData] = React.useState();

	const editDeadline = (item: any) => {
		setItemData(item);

		setShowEditDeadlineModal(true);
	};

	// State to keep track of Additional Training Id
	const [trainingId, setTrainingId] = React.useState<string>('');

	React.useEffect(() => {
		setTimeout(() => {
			setFormErrorMessage('');
		}, 5000);
	}, [formErrorMessage]);

	// Logic to work out organisation logo
	const activeProfileId = useAppSelector((state) => state.user.activeProfileId);
	const profiles = useAppSelector((state) => state.user.profiles);
	const activeProfile = profiles?.find((item) => item.id === activeProfileId);
	const organisationLogo = activeProfile?.image;

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{isOrganisationAdmin && viewTrainingAsAdmin ? (
									<>
										<p className="sub-grey-text mbtom-30">
											Below is a list of child safety training modules available to your organisation to assign to
											leaders, staff, parents, children, volunteers and visitors. Choose which training modules you
											would like users to complete and set a completion deadline.
										</p>

										<Alert color="info">
											<div className="alert-icon">
												<SwitchUserIcon />
											</div>

											<div className="alert-content">
												<p>
													As a <strong>Leader</strong>, you can manage your organisation’s training by assigning
													training modules to users from the available menu for completion by a deadline.
												</p>

												<p>
													You can also complete training modules <strong>assigned to you</strong> by your organisation.
													Click on ‘View my training’ to switch roles.
												</p>
											</div>

											<Button className="btn-primary small" onClick={() => switchRole()}>
												<SwitchIcon className="link-icon" /> View my training
											</Button>
										</Alert>

										<div className="page-header-section training-page">
											<div className="header-left">
												<Button
													onClick={() => setActiveTab('leadership')}
													className={`staff-category ${activeTab === 'leadership' ? 'active' : ''}`}
												>
													Leadership
												</Button>

												<Button
													onClick={() => setActiveTab('staff')}
													className={`staff-category ${activeTab === 'staff' ? 'active' : ''}`}
												>
													Staff & Volunteers
												</Button>

												<Button
													onClick={() => setActiveTab('Volunteers')}
													className={`staff-category ${activeTab === 'Volunteers' ? 'active' : ''}`}
												>
													Parent Volunteers
												</Button>

												<Button
													onClick={() => setActiveTab('parents')}
													className={`staff-category ${activeTab === 'parents' ? 'active' : ''}`}
												>
													Parents
												</Button>

												<Button
													onClick={() => setActiveTab('children')}
													className={`staff-category ${activeTab === 'children' ? 'active' : ''}`}
												>
													Children
												</Button>

												<Button
													onClick={() => setActiveTab('visitors')}
													className={`staff-category ${activeTab === 'visitors' ? 'active' : ''}`}
												>
													Visitors
												</Button>
											</div>
										</div>

										{/* LEADERSHIP TAB */}
										{activeTab === 'leadership' && (
											<>
												<section className="info-section">
													<div className="section-header">
														<h2 className="header-name color-dark">Assigned</h2>
													</div>

													{leadership && leadership.length > 0 ? (
														leadership.map((item) => {
															const isOwnTraining =
																modules && modules.filter((i) => i.id === item.moduleId)[0].editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-4 col-md-4 detail-name">
																					{modules && modules.filter((i) => i.id === item.moduleId)[0].title}

																					<p className="text-subtitle">
																						{modules && modules.filter((i) => i.id === item.moduleId)[0].subtitle}
																					</p>
																				</div>

																				<div className="col-md-4 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{modules &&
																						modules.filter((i) => i.id === item.moduleId)[0] &&
																						modules.filter((i) => i.id === item.moduleId)[0].standards!.length > 1
																							? 'Multiple selected'
																							: modules!.filter((i) => i.id === item.moduleId)[0].standards}
																					</p>
																				</div>

																				<div className="col-4 col-md-4 text-category-detail">
																					Deadline
																					<p onClick={() => editDeadline(item)} className="sub-purple-text pointer">
																						{item.deadlineTimeFrameType ? (
																							trainingTimeFrames?.filter(
																								({ value }) => value === item.deadlineTimeFrameType
																							)[0].label
																						) : item.deadlineDate ? (
																							<DisplayDate>{item.deadlineDate}</DisplayDate>
																						) : (
																							'Add deadline'
																						)}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text remove" to={`/training/${item.moduleId}`}>
																				View
																			</Link>

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(
																							`${modules && modules.filter((i) => i.id === item.moduleId)[0].title}`
																						);

																						setShowRemoveModal(true);
																					}}
																				>
																					Remove
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})
													) : (
														<div className="empty-data-notification">
															You have not assigned any training modules to the <strong>‘Leadership’</strong> group.
															Select from the available modules below that you wish to assign to this group.
														</div>
													)}
												</section>

												<section className="info-section">
													<div className="section-header">
														<div className="name-group">
															<h2 className="header-name color-dark ">Available</h2>

															{isOrganisationActive && (
																<Link className="btn btn-secondary btn-small" to="/training-submit">
																	<PlusIcon className="link-icon" /> Add new training module
																</Link>
															)}
														</div>

														{isOrganisationActive && (
															<Button
																className="btn-text pdr-0"
																disabled={availableLeadershipModules?.length === 0}
																onClick={() => {
																	const allIDs =
																		(availableLeadershipModules &&
																			availableLeadershipModules.length > 0 &&
																			availableLeadershipModules.map((item) => item.id || '')) ||
																		[];

																	setAssignedTrainingIdArray(allIDs);

																	setShowAssignAllTrainingModal(true);
																}}
															>
																Assign all
															</Button>
														)}
													</div>

													{availableLeadershipModules &&
														availableLeadershipModules.length > 0 &&
														availableLeadershipModules.map((item) => {
															const isOwnTraining = item?.editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-10 col-md-6 detail-name">
																					{item.title}
																					<p className="text-subtitle">{item.subtitle}</p>
																				</div>

																				<div className="col-md-6 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{item.standards && item.standards?.length > 1
																							? 'Multiple selected'
																							: item.standards}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOwnTraining && isOrganisationActive ? (
																				<Link className="btn-text remove" to={`/training-submit/${item.id}`}>
																					Edit
																				</Link>
																			) : (
																				<Link className="btn-text remove" to={`/training/${item.id}`}>
																					View
																				</Link>
																			)}

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(item.title || '');

																						setShowAssignTrainingModal(true);
																					}}
																				>
																					Assign
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
												</section>
											</>
										)}

										{/* STAFF TAB */}
										{activeTab === 'staff' && (
											<>
												<section className="info-section">
													<div className="section-header">
														<h2 className="header-name color-dark">Assigned</h2>
													</div>

													{staff && staff.length > 0 ? (
														staff.map((item, index) => {
															const isOwnTraining =
																modules && modules.filter((i) => i.id === item.moduleId)[0].editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-4 col-md-4 detail-name">
																					{modules && modules.filter((i) => i.id === item.moduleId)[0].title}
																					<p className="text-subtitle">
																						{modules && modules.filter((i) => i.id === item.moduleId)[0].subtitle}
																					</p>
																				</div>

																				<div className="col-md-4 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{modules &&
																						modules.filter((i) => i.id === item.moduleId)[0] &&
																						modules.filter((i) => i.id === item.moduleId)[0].standards!.length > 1
																							? 'Multiple selected'
																							: modules && modules.filter((i) => i.id === item.moduleId)[0].standards}
																					</p>
																				</div>

																				<div className="col-4 col-md-4 text-category-detail">
																					Deadline
																					<p onClick={() => editDeadline(item)} className="sub-purple-text pointer">
																						{item.deadlineTimeFrameType ? (
																							trainingTimeFrames?.filter(
																								({ value }) => value === item.deadlineTimeFrameType
																							)[0].label
																						) : item.deadlineDate ? (
																							<DisplayDate>{item.deadlineDate}</DisplayDate>
																						) : (
																							'Add deadline'
																						)}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text remove" to={`/training/${item.moduleId}`}>
																				View
																			</Link>

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(
																							`${modules && modules.filter((i) => i.id === item.moduleId)[0].title}`
																						);

																						setShowRemoveModal(true);
																					}}
																				>
																					Remove
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})
													) : (
														<div className="empty-data-notification">
															You have not assigned any training modules to the <strong>‘Staff’</strong> group. Select
															from the available modules below that you wish to assign to this group.
														</div>
													)}
												</section>

												<section className="info-section">
													<div className="section-header">
														<div className="name-group">
															<h2 className="header-name color-dark ">Available</h2>

															{isOrganisationActive && (
																<Link className="btn btn-secondary btn-small" to="/training-submit">
																	<PlusIcon className="link-icon" /> Add new training module
																</Link>
															)}
														</div>

														{isOrganisationActive && (
															<Button
																className="btn-text pdr-0"
																disabled={availableStaffModules?.length === 0}
																onClick={() => {
																	const allIDs =
																		(availableStaffModules && availableStaffModules.map((item) => item.id || '')) || [];

																	setAssignedTrainingIdArray(allIDs);

																	setShowAssignAllTrainingModal(true);
																}}
															>
																Assign all
															</Button>
														)}
													</div>

													{availableStaffModules &&
														availableStaffModules.map((item) => {
															const isOwnTraining = item?.editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-10 col-md-6 detail-name">
																					{item.title}
																					<p className="text-subtitle">{item.subtitle}</p>
																				</div>

																				<div className="col-md-6 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{item.standards && item.standards?.length > 1
																							? 'Multiple selected'
																							: item.standards}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOwnTraining && isOrganisationActive ? (
																				<Link className="btn-text remove" to={`/training-submit/${item.id}`}>
																					Edit
																				</Link>
																			) : (
																				<Link className="btn-text remove" to={`/training/${item.id}`}>
																					View
																				</Link>
																			)}

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(item.title || '');

																						setShowAssignTrainingModal(true);
																					}}
																				>
																					Assign
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
												</section>
											</>
										)}

										{/* PARENTS TAB */}
										{activeTab === 'parents' && (
											<>
												<section className="info-section">
													<div className="section-header">
														<h2 className="header-name color-dark">Assigned</h2>
													</div>

													{parents && parents.length > 0 ? (
														parents.map((item) => {
															const isOwnTraining =
																modules && modules.filter((i) => i.id === item.moduleId)[0].editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-4 col-md-4 detail-name">
																					{modules && modules.filter((i) => i.id === item.moduleId)[0].title}
																					<p className="text-subtitle">
																						{modules && modules.filter((i) => i.id === item.moduleId)[0].subtitle}
																					</p>
																				</div>

																				<div className="col-md-4 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{modules &&
																						modules.filter((i) => i.id === item.moduleId)[0] &&
																						modules.filter((i) => i.id === item.moduleId)[0].standards!.length > 1
																							? 'Multiple selected'
																							: modules && modules.filter((i) => i.id === item.moduleId)[0].standards}
																					</p>
																				</div>

																				<div className="col-4 col-md-4 text-category-detail">
																					Deadline
																					<p onClick={() => editDeadline(item)} className="sub-purple-text pointer">
																						{item.deadlineTimeFrameType ? (
																							trainingTimeFrames?.filter(
																								({ value }) => value === item.deadlineTimeFrameType
																							)[0].label
																						) : item.deadlineDate ? (
																							<DisplayDate>{item.deadlineDate}</DisplayDate>
																						) : (
																							'Add deadline'
																						)}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text remove" to={`/training/${item.moduleId}`}>
																				View
																			</Link>

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(
																							`${modules && modules.filter((i) => i.id === item.moduleId)[0].title}`
																						);

																						setShowRemoveModal(true);
																					}}
																				>
																					Remove
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})
													) : (
														<div className="empty-data-notification">
															You have not assigned any training modules to the <strong>‘Parents’</strong> group. Select
															from the available modules below that you wish to assign to this group.
														</div>
													)}
												</section>

												<section className="info-section">
													<div className="section-header">
														<div className="name-group">
															<h2 className="header-name color-dark ">Available</h2>

															{isOrganisationActive && (
																<Link className="btn btn-secondary btn-small" to="/training-submit">
																	<PlusIcon className="link-icon" /> Add new training module
																</Link>
															)}
														</div>

														{isOrganisationActive && (
															<Button
																className="btn-text pdr-0"
																disabled={availableParentModules?.length === 0}
																onClick={() => {
																	const allIDs =
																		(availableParentModules && availableParentModules.map((item) => item.id || '')) ||
																		[];

																	setAssignedTrainingIdArray(allIDs);

																	setShowAssignAllTrainingModal(true);
																}}
															>
																Assign all
															</Button>
														)}
													</div>

													{availableParentModules &&
														availableParentModules.length > 0 &&
														availableParentModules.map((item) => {
															const isOwnTraining = item?.editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-10 col-md-6 detail-name">
																					{item.title}
																					<p className="text-subtitle">{item.subtitle}</p>
																				</div>

																				<div className="col-md-6 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{item.standards && item.standards?.length > 1
																							? 'Multiple selected'
																							: item.standards}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOwnTraining && isOrganisationActive ? (
																				<Link className="btn-text remove" to={`/training-submit/${item.id}`}>
																					Edit
																				</Link>
																			) : (
																				<Link className="btn-text remove" to={`/training/${item.id}`}>
																					View
																				</Link>
																			)}

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(item.title || '');

																						setShowAssignTrainingModal(true);
																					}}
																				>
																					Assign
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
												</section>
											</>
										)}

										{/* VOLUNTEERS TAB */}
										{activeTab === 'Volunteers' && (
											<>
												<section className="info-section">
													<div className="section-header">
														<h2 className="header-name color-dark">Assigned</h2>
													</div>

													{volunteers && volunteers.length > 0 ? (
														volunteers.map((item: any) => {
															const isOwnTraining =
																modules && modules.filter((i) => i.id === item.moduleId)[0].editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-4 col-md-4 detail-name">
																					{modules && modules.filter((i) => i.id === item.moduleId)[0].title}
																					<p className="text-subtitle">
																						{modules && modules.filter((i) => i.id === item.moduleId)[0].subtitle}
																					</p>
																				</div>

																				<div className="col-md-4 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{modules &&
																						modules.filter((i) => i.id === item.moduleId)[0] &&
																						modules.filter((i) => i.id === item.moduleId)[0].standards!.length > 1
																							? 'Multiple selected'
																							: modules && modules.filter((i) => i.id === item.moduleId)[0].standards}
																					</p>
																				</div>

																				<div className="col-4 col-md-4 text-category-detail">
																					Deadline
																					<p onClick={() => editDeadline(item)} className="sub-purple-text pointer">
																						{item.deadlineTimeFrameType ? (
																							trainingTimeFrames?.filter(
																								({ value }) => value === item.deadlineTimeFrameType
																							)[0].label
																						) : item.deadlineDate ? (
																							<DisplayDate>{item.deadlineDate}</DisplayDate>
																						) : (
																							'Add deadline'
																						)}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text remove" to={`/training/${item.moduleId}`}>
																				View
																			</Link>

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(
																							`${modules && modules.filter((i) => i.id === item.moduleId)[0].title}`
																						);

																						setShowRemoveModal(true);
																					}}
																				>
																					Remove
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})
													) : (
														<div className="empty-data-notification">
															You have not assigned any training modules to the <strong>‘Parent Volunteers’</strong>{' '}
															group. Select from the available modules below that you wish to assign to this group.
														</div>
													)}
												</section>

												<section className="info-section">
													<div className="section-header">
														<div className="name-group">
															<h2 className="header-name color-dark ">Available</h2>

															{isOrganisationActive && (
																<Link className="btn btn-secondary btn-small" to="/training-submit">
																	<PlusIcon className="link-icon" /> Add new training module
																</Link>
															)}
														</div>

														{isOrganisationActive && (
															<Button
																className="btn-text pdr-0"
																disabled={availableVolunteerModules?.length === 0}
																onClick={() => {
																	const allIDs = availableVolunteerModules
																		? availableVolunteerModules.map((item) => item.id || '')
																		: [];

																	setAssignedTrainingIdArray(allIDs);

																	setShowAssignAllTrainingModal(true);
																}}
															>
																Assign all
															</Button>
														)}
													</div>

													{availableVolunteerModules &&
														availableVolunteerModules.length > 0 &&
														availableVolunteerModules.map((item) => {
															const isOwnTraining = item?.editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-10 col-md-6 detail-name">
																					{item.title}
																					<p className="text-subtitle">{item.subtitle}</p>
																				</div>

																				<div className="col-md-6 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{item.standards && item.standards?.length > 1
																							? 'Multiple selected'
																							: item.standards}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOwnTraining && isOrganisationActive ? (
																				<Link className="btn-text remove" to={`/training-submit/${item.id}`}>
																					Edit
																				</Link>
																			) : (
																				<Link className="btn-text remove" to={`/training/${item.id}`}>
																					View
																				</Link>
																			)}

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(item.title || '');

																						setShowAssignTrainingModal(true);
																					}}
																				>
																					Assign
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
												</section>
											</>
										)}

										{/* CHILDREN TAB */}
										{activeTab === 'children' && (
											<>
												<section className="info-section">
													<div className="section-header">
														<h2 className="header-name color-dark">Assigned</h2>
													</div>

													{children && children.length > 0 ? (
														children.map((item, index) => {
															const isOwnTraining =
																modules && modules.filter((i) => i.id === item.moduleId)[0].editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-4 col-md-4 detail-name">
																					{modules && modules.filter((i) => i.id === item.moduleId)[0].title}
																					<p className="text-subtitle">
																						{modules && modules.filter((i) => i.id === item.moduleId)[0].subtitle}
																					</p>
																				</div>

																				<div className="col-md-4 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{modules &&
																						modules.filter((i) => i.id === item.moduleId)[0] &&
																						modules.filter((i) => i.id === item.moduleId)[0].standards!.length > 1
																							? 'Multiple selected'
																							: modules && modules.filter((i) => i.id === item.moduleId)[0].standards}
																					</p>
																				</div>

																				<div className="col-4 col-md-4 text-category-detail">
																					Deadline
																					<p onClick={() => editDeadline(item)} className="sub-purple-text pointer">
																						{item.deadlineTimeFrameType ? (
																							trainingTimeFrames?.filter(
																								({ value }) => value === item.deadlineTimeFrameType
																							)[0].label
																						) : item.deadlineDate ? (
																							<DisplayDate>{item.deadlineDate}</DisplayDate>
																						) : (
																							'Add deadline'
																						)}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text remove" to={`/training/${item.moduleId}`}>
																				View
																			</Link>

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(
																							`${modules && modules.filter((i) => i.id === item.moduleId)[0].title}`
																						);

																						setShowRemoveModal(true);
																					}}
																				>
																					Remove
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})
													) : (
														<div className="empty-data-notification">
															You have not assigned any training modules to the <strong>‘Children’</strong> group.
															Select from the available modules below that you wish to assign to this group.
														</div>
													)}
												</section>

												<section className="info-section">
													<div className="section-header">
														<div className="name-group">
															<h2 className="header-name color-dark ">Available</h2>

															{isOrganisationActive && (
																<Link className="btn btn-secondary btn-small" to="/training-submit">
																	<PlusIcon className="link-icon" /> Add new training module
																</Link>
															)}
														</div>

														{isOrganisationActive && (
															<Button
																className="btn-text pdr-0"
																disabled={availableChildrenModules?.length === 0}
																onClick={() => {
																	const allIDs =
																		(availableChildrenModules &&
																			availableChildrenModules.map((item) => item.id || '')) ||
																		[];

																	setAssignedTrainingIdArray(allIDs);

																	setShowAssignAllTrainingModal(true);
																}}
															>
																Assign all
															</Button>
														)}
													</div>

													{availableChildrenModules &&
														availableChildrenModules.length > 0 &&
														availableChildrenModules.map((item) => {
															const isOwnTraining = item?.editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-10 col-md-6 detail-name">
																					{item.title}
																					<p className="text-subtitle">{item.subtitle}</p>
																				</div>

																				<div className="col-md-6 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{item.standards && item.standards?.length > 1
																							? 'Multiple selected'
																							: item.standards}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOwnTraining && isOrganisationActive ? (
																				<Link className="btn-text remove" to={`/training-submit/${item.id}`}>
																					Edit
																				</Link>
																			) : (
																				<Link className="btn-text remove" to={`/training/${item.id}`}>
																					View
																				</Link>
																			)}

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(item.title || '');

																						setShowAssignTrainingModal(true);
																					}}
																				>
																					Assign
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
												</section>
											</>
										)}

										{/* Visitors TAB */}
										{activeTab === 'visitors' && (
											<>
												<section className="info-section">
													<div className="section-header">
														<h2 className="header-name color-dark">Assigned</h2>
													</div>

													{visitors && visitors.length > 0 ? (
														visitors.map((item) => {
															const isOwnTraining =
																modules && modules.filter((i) => i.id === item.moduleId)[0].editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-4 col-md-4 detail-name">
																					{modules && modules.filter((i) => i.id === item.moduleId)[0].title}
																					<p className="text-subtitle">
																						{modules && modules.filter((i) => i.id === item.moduleId)[0].subtitle}
																					</p>
																				</div>

																				<div className="col-md-4 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{modules &&
																						modules.filter((i) => i.id === item.moduleId)[0] &&
																						modules.filter((i) => i.id === item.moduleId)[0].standards!.length > 1
																							? 'Multiple selected'
																							: modules && modules.filter((i) => i.id === item.moduleId)[0].standards}
																					</p>
																				</div>

																				<div className="col-4 col-md-4 text-category-detail">
																					Deadline
																					<p onClick={() => editDeadline(item)} className="sub-purple-text pointer">
																						{item.deadlineTimeFrameType ? (
																							trainingTimeFrames?.filter(
																								({ value }) => value === item.deadlineTimeFrameType
																							)[0].label
																						) : item.deadlineDate ? (
																							<DisplayDate>{item.deadlineDate}</DisplayDate>
																						) : (
																							'Add deadline'
																						)}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text remove" to={`/training/${item.moduleId}`}>
																				View
																			</Link>

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(
																							`${modules && modules.filter((i) => i.id === item.moduleId)[0].title}`
																						);

																						setShowRemoveModal(true);
																					}}
																				>
																					Remove
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})
													) : (
														<div className="empty-data-notification">
															You have not assigned any training modules to the <strong>‘Visitors’</strong> group.
															Select from the available modules below that you wish to assign to this group.
														</div>
													)}
												</section>

												<section className="info-section">
													<div className="section-header">
														<div className="name-group">
															<h2 className="header-name color-dark ">Available</h2>

															{isOrganisationActive && (
																<Link className="btn btn-secondary btn-small" to="/training-submit">
																	<PlusIcon className="link-icon" /> Add new training module
																</Link>
															)}
														</div>

														{isOrganisationActive && (
															<Button
																className="btn-text pdr-0"
																disabled={availableVisitorModules?.length === 0}
																onClick={() => {
																	const allIDs =
																		(availableVisitorModules &&
																			availableVisitorModules.length > 0 &&
																			availableVisitorModules.map((item) => item.id || '')) ||
																		[];

																	setAssignedTrainingIdArray(allIDs);

																	setShowAssignAllTrainingModal(true);
																}}
															>
																Assign all
															</Button>
														)}
													</div>

													{availableVisitorModules &&
														availableVisitorModules.length > 0 &&
														availableVisitorModules.map((item) => {
															const isOwnTraining = item?.editable;

															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			{isOwnTraining ? (
																				<StatusIcon statusType="training-plus" />
																			) : (
																				<StatusIcon statusType="training" />
																			)}
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-10 col-md-6 detail-name">
																					{item.title}
																					<p className="text-subtitle">{item.subtitle}</p>
																				</div>

																				<div className="col-md-6 d-none d-md-block text-category-detail">
																					Child Safe Standard
																					<p className="text-subtitle">
																						{item.standards && item.standards?.length > 1
																							? 'Multiple selected'
																							: item.standards}
																					</p>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOwnTraining && isOrganisationActive ? (
																				<Link className="btn-text remove" to={`/training-submit/${item.id}`}>
																					Edit
																				</Link>
																			) : (
																				<Link className="btn-text remove" to={`/training/${item.id}`}>
																					View
																				</Link>
																			)}

																			{isOrganisationActive && (
																				<Button
																					className="btn-text pdr-0"
																					onClick={() => {
																						setAssignedTrainingId(item.id || '');

																						setAssignedTrainingTitle(item.title || '');

																						setShowAssignTrainingModal(true);
																					}}
																				>
																					Assign
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
												</section>
											</>
										)}
									</>
								) : (
									<>
										<p className="sub-grey-text mbtom-30">
											Your organisation has assigned the following child safety training modules to you for completion.
										</p>

										{isOrganisationAdmin && (
											<Alert color="info">
												<div className="alert-icon">
													<SwitchUserIcon />
												</div>

												<div className="alert-content">
													<p>
														Below are the training modules <strong>assigned to you</strong> by your organisation for
														completion.
													</p>

													<p>
														As a <strong>Leader</strong>, you can also manage your organisation’s training. Click on
														‘Manage training’ to switch roles.
													</p>
												</div>

												<Button className="btn-primary small" onClick={() => switchRole()}>
													<SwitchIcon className="link-icon" /> Manage training
												</Button>
											</Alert>
										)}

										{/* OVERDUE TRAINING */}
										{overdueUserTraining && overdueUserTraining.length > 0 && (
											<section className="info-section">
												<div className="section-header">
													<h2 className="header-name color-dark">Overdue</h2>
												</div>

												{overdueUserTraining.map((item) => {
													return (
														<div key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0">
																	<StatusIcon statusType="danger" />
																</div>

																<div className="col-md-auto full-width">
																	<div className="row detail-info">
																		<div className="col-10 col-md-4 detail-name">
																			{item.title}
																			<p className="text-subtitle">{item.subtitle}</p>
																		</div>

																		<div className="col-md-4 d-none d-md-block text-category-detail">
																			Child Safe Standard
																			<p className="text-subtitle">
																				{item.standards && item.standards?.length > 1
																					? 'Multiple selected'
																					: item.standards}
																			</p>
																		</div>

																		<div className="col-md-4 d-none d-md-block text-category-detail">
																			Deadline
																			<p className="text-subtitle">
																				{item.deadline ? <DisplayDate>{item.deadline}</DisplayDate> : 'No deadline'}
																			</p>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text" to={`/training/${item.moduleId}`}>
																		View
																	</Link>
																</div>
															</div>
														</div>
													);
												})}
											</section>
										)}

										{/* TODO TRAINING */}
										{availableUserTraining && availableUserTraining.length > 0 && (
											<section className="info-section">
												<div className="section-header">
													<h2 className="header-name color-dark">To-do</h2>
												</div>

												{availableUserTraining.map((item) => {
													return (
														<div key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0">
																	<StatusIcon statusType="todo" />
																</div>

																<div className="col-md-auto full-width">
																	<div className="row detail-info">
																		<div className="col-10 col-md-4 detail-name">
																			{item.title}
																			<p className="text-subtitle">{item.subtitle}</p>
																		</div>

																		<div className="col-md-4 d-none d-md-block text-category-detail">
																			Child Safe Standard
																			<p className="text-subtitle">
																				{item.standards && item.standards?.length > 1
																					? 'Multiple selected'
																					: item.standards}
																			</p>
																		</div>

																		<div className="col-md-4 d-none d-md-block text-category-detail">
																			Deadline
																			<p className="text-subtitle">
																				{item.deadline ? <DisplayDate>{item.deadline}</DisplayDate> : 'No deadline'}
																			</p>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text" to={`/training/${item.moduleId}`}>
																		View
																	</Link>
																</div>
															</div>
														</div>
													);
												})}
											</section>
										)}

										{/* COMPLETED TRAINING */}
										{completedUserTraining && completedUserTraining.length > 0 && (
											<section className="info-section">
												<div className="section-header">
													<h2 className="header-name color-dark ">Completed</h2>
												</div>

												{completedUserTraining.map((item) => {
													return (
														<div key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0">
																	<StatusIcon statusType="success" />
																</div>

																<div className="col-md-auto full-width">
																	<div className="row detail-info">
																		<div className="col-10 col-md-6 detail-name">
																			{item.title}
																			<p className="text-subtitle">{item.subtitle}</p>
																		</div>

																		<div className="col-md-6 d-none d-md-block text-category-detail">
																			Completed
																			<p className="text-subtitle">{<DisplayDate>{item.completed}</DisplayDate>}</p>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text" to={`/training/${item.moduleId}`}>
																		View
																	</Link>
																</div>
															</div>
														</div>
													);
												})}
											</section>
										)}

										{/* ADDITIONAL TRAINING */}
										<section className="info-section">
											<div className="section-header no-border">
												<h2 className="header-name color-dark ">Additional training</h2>
											</div>

											<p className="notification-text border-bottom mbtom-30">
												If you’ve completed additional training outside ChildAware, add it here to keep a record.
											</p>

											{additionalTrainings &&
												additionalTrainings.length > 0 &&
												additionalTrainings.map((item) => {
													return (
														<div key={item.id} className="row-container">
															<div className="row detail-row first-border">
																<div className="col-auto pdl-0 pdr-0">
																	<StatusIcon statusType="todo" />
																</div>

																<div className="col-md-auto full-width">
																	<div className="row detail-info">
																		<div className="col-10 col-md-6 detail-name">{item.title}</div>

																		<div className="col-md-6 d-none d-md-block text-category-detail">
																			Completed
																			<p className="text-subtitle">
																				<DisplayDate>{item.dateOfTraining}</DisplayDate>
																			</p>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Button
																		className="btn-text"
																		onClick={() => {
																			setTrainingId(item.id || '');
																			setShowAddTrainingModal(true);
																		}}
																	>
																		View
																	</Button>
																</div>
															</div>
														</div>
													);
												})}

											{isOrganisationActive && (
												<Button
													className="btn-primary training-btn"
													onClick={() => {
														setTrainingId('');
														setShowAddTrainingModal(true);
													}}
												>
													Add additional training
												</Button>
											)}
										</section>
									</>
								)}

								<ScrollToTopButton
									offsetValue={!viewTrainingAsAdmin ? 100 : 0}
									extraClass={viewTrainingAsAdmin ? 'mtop-30' : ''}
								/>

								<GlobalModal
									isOpen={showAddTrainingModal}
									setIsOpen={setShowAddTrainingModal}
									titleText="Additional training"
								>
									<AddAdditionalTrainingModal trainingId={trainingId} getUserTrainingData={getUserTrainingData} />
								</GlobalModal>

								<GlobalModal
									isOpen={showEditDeadlineModal}
									setIsOpen={setShowEditDeadlineModal}
									titleText="Edit Training Deadline"
								>
									<EditDeadlineModal item={itemData} getTrainingData={getTrainingData} />
								</GlobalModal>

								<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove Training">
									<RemoveModal
										assignedTrainingId={assignedTrainingId}
										assignedTrainingTitle={assignedTrainingTitle}
										getTrainingData={getTrainingData}
									/>
								</GlobalModal>

								<GlobalModal
									isOpen={showAssignTrainingModal}
									setIsOpen={setShowAssignTrainingModal}
									titleText={`Assign training to ${activeTab}`}
								>
									<AssignTrainingModal
										assignedTrainingId={assignedTrainingId}
										assignedTrainingTitle={assignedTrainingTitle}
										getTrainingData={getTrainingData}
										profileType={activeTab as DomainTrainingProfileType}
										setActiveTab={setActiveTab}
									/>
								</GlobalModal>

								<GlobalModal
									isOpen={showAssignAllTrainingModal}
									setIsOpen={setShowAssignAllTrainingModal}
									titleText={`Assign training to ${activeTab}`}
								>
									<AssignAllTrainingModal
										assignedTrainingIdArray={assignedTrainingIdArray}
										getTrainingData={getTrainingData}
										profileType={activeTab as DomainTrainingProfileType}
										setActiveTab={setActiveTab}
									/>
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default Training;
